/**
 * Single morphing button — Preload → loading → Transition — driven only by backend `performControl`
 * (GET + `live-context-perform-control` WS store overlay). See `useMergedPerformControl`.
 */

import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { TbLoader3, TbSwitch3 } from 'react-icons/tb';
import { useLayerCommandMutation } from '@/state/mutations/player/use-layer-command';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import { performControlActionButtonSx } from './preload-transition-shared';
import { useMergedPerformControl } from '@/hooks/use-merged-perform-control';
import type { StagedMediaProcessingBlock } from '@/helpers/media-processing-insight.helper';

interface PreloadTransitionButtonsProps {
  entityRef: string;
  liveContextState: LiveContextStateDto | undefined;
  selectedItemIds: string[];
  disabled?: boolean;
  processingBlock?: StagedMediaProcessingBlock;
}

export function PreloadTransitionButtons({
  entityRef,
  liveContextState,
  selectedItemIds,
  disabled = false,
  processingBlock,
}: PreloadTransitionButtonsProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const layerCommandMutation = useLayerCommandMutation({
    onError: (err) => {
      notification.notifyError(
        err instanceof Error ? err.message : t('flows.detail.perform.preloadTransition.transitionFailed'),
      );
    },
  });

  const merged = useMergedPerformControl(entityRef, liveContextState?.performControl);
  const hasSelection = selectedItemIds.length > 0;
  const aggregate = merged?.aggregate;
  const suggested = aggregate?.suggestedLayerCommand;
  const policyPhase = aggregate?.phase;

  const showPerform =
    hasSelection && merged != null && (policyPhase === 'WAIT' || suggested === 'preload' || suggested === 'show');

  const preloadMutationPending =
    layerCommandMutation.isPending && layerCommandMutation.variables?.action === 'perform' && suggested === 'preload';
  const showMutationPending =
    layerCommandMutation.isPending && layerCommandMutation.variables?.action === 'perform' && suggested === 'show';

  const handlePerform = () => {
    layerCommandMutation.mutate({
      action: 'perform',
      entityRef,
    });
  };

  const stableButtonSx = {
    ...performControlActionButtonSx,
  } as const;

  const isProcessingBlocked = processingBlock?.blocked === true;
  const processingBlockMessage =
    isProcessingBlocked && processingBlock.reasonKey
      ? t(processingBlock.reasonKey, { title: processingBlock.mediaTitle ?? '' })
      : undefined;

  if (!showPerform && !isProcessingBlocked) return null;

  const isWait = policyPhase === 'WAIT';
  const isPreloadAction = suggested === 'preload';
  const isShowAction = suggested === 'show';

  let primaryPhase: 'preload' | 'preload-loading' | 'transition' | 'transition-loading';
  if (isWait) {
    const preloadishWait = aggregate?.waitHint?.kind === 'preload' || suggested === 'preload';
    primaryPhase = preloadishWait ? 'preload-loading' : 'transition-loading';
  } else if (isPreloadAction) {
    primaryPhase = preloadMutationPending ? 'preload-loading' : 'preload';
  } else if (isShowAction) {
    primaryPhase = showMutationPending ? 'transition-loading' : 'transition';
  } else {
    primaryPhase = 'transition';
  }

  const isBusy = isWait || (isPreloadAction && preloadMutationPending) || (isShowAction && showMutationPending);

  const tooltipTitle =
    primaryPhase === 'preload'
      ? t('flows.detail.perform.preloadTransition.tooltipPreload')
      : primaryPhase === 'preload-loading'
        ? t('flows.detail.perform.preloadTransition.tooltipPreloading')
        : primaryPhase === 'transition-loading'
          ? t('flows.detail.perform.preloadTransition.tooltipApplyingTransition')
          : isShowAction && policyPhase === 'TRANSITION'
            ? t('flows.detail.perform.preloadTransition.tooltipAllReady')
            : isShowAction
              ? t('flows.detail.perform.preloadTransition.tooltipTransition')
              : t('flows.detail.perform.preloadTransition.tooltipTransition');

  const transitionColor: 'success' | 'primary' = isShowAction && policyPhase === 'TRANSITION' ? 'success' : 'primary';

  const onClick =
    isWait || (isPreloadAction && preloadMutationPending) || (isShowAction && showMutationPending)
      ? undefined
      : isPreloadAction || isShowAction
        ? handlePerform
        : undefined;

  return (
    <Box
      sx={(theme) => ({
        'flex': '1 1 0%',
        'minWidth': 0,
        'maxWidth': {
          xs: `calc((100% - ${theme.spacing(0.5)}) / 2)`,
          sm: `calc((100% - ${theme.spacing(1)}) / 2)`,
        },
        '&:only-child': { maxWidth: '100%' },
        'display': 'flex',
        'flexDirection': 'column',
        'justifyContent': 'center',
        'alignItems': 'center',
        'gap': 0.5,
      })}
    >
      {isProcessingBlocked && processingBlockMessage && (
        <Typography variant="caption" color="warning.main" sx={{ px: 0.5, textAlign: 'center', lineHeight: 1.3 }}>
          {processingBlockMessage}
        </Typography>
      )}
      {showPerform && (
        <Tooltip title={isProcessingBlocked ? processingBlockMessage : tooltipTitle}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              minWidth: 0,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              size="medium"
              sx={{
                ...stableButtonSx,
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                lineHeight: 1.2,
                py: 0.5,
              }}
              color={primaryPhase === 'transition' ? transitionColor : 'primary'}
              startIcon={
                isBusy ? undefined : primaryPhase === 'preload' ? <TbLoader3 size={18} /> : <TbSwitch3 size={18} />
              }
              onClick={onClick}
              disabled={isBusy || disabled || isProcessingBlocked || !onClick || layerCommandMutation.isPending}
              aria-busy={isBusy}
              aria-label={
                primaryPhase === 'preload-loading'
                  ? t('flows.detail.perform.preloadTransition.ariaPreloading')
                  : primaryPhase === 'transition-loading'
                    ? t('flows.detail.perform.preloadTransition.ariaTransitionInProgress')
                    : undefined
              }
            >
              {isBusy ? (
                <CircularProgress size={18} color="inherit" />
              ) : primaryPhase === 'preload' ? (
                t('flows.detail.perform.preloadTransition.buttonPreload')
              ) : (
                t('flows.detail.perform.preloadTransition.buttonTransition')
              )}
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
